import { CSSProperties } from 'react';
import { Button } from '@ets/design-system';
import { Tag } from '@ets/design-system';
import { FormattedMessage, useIntl } from 'react-intl';

import { useDispatch, useSelector } from '_common/hooks';
import { clearFilterIdentity, clearFilterValue, selectHasFilters } from '../../FilterSlice';

import {
  DateRange,
  FilterIdentity,
  FilterName,
  FILTERS,
  FilterType,
  TRANSLATIONS,
  TYPES,
} from '../../FilterController';

import styles from './FilterDisplay.module.scss';
import { useFormattedDate } from '_common/hooks/useFormattedDate';

type FilterDisplayProps = {
  identity: FilterIdentity;
  direction?: 'row' | 'column';
  margin?: CSSProperties['margin'];
};

const FilterDisplay = ({
  identity,
  direction = 'row',
  margin = '3rem 0 0 0',
}: FilterDisplayProps) => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const formattedDate = useFormattedDate();
  const filters = useSelector((state) => state.filters[identity]);
  const hasActiveFilters = useSelector((state) => selectHasFilters(state, state.filters[identity]));
  const nonGenericIdentity = (identity.match(/[a-zA-Z]*-/i)?.[0] as FilterIdentity) ?? identity;

  if (!hasActiveFilters) {
    return null;
  }

  const handleClearFilter = (filter: FilterName, value?: ValueOf<FilterType>) => {
    dispatch(clearFilterValue({ identity, filter, value }));
  };

  const handleClearIdentity = () => {
    dispatch(clearFilterIdentity({ identity }));
  };

  const renderDateRangeLabel = ({ startISO, endISO }: { startISO?: ISODate; endISO?: ISODate }) => {
    const formattedStart = startISO ? formattedDate(startISO, 'numeric') : undefined;
    const formattedEnd = endISO ? formattedDate(endISO, 'numeric') : undefined;

    if (formattedStart && formattedEnd) {
      return intl.formatMessage(
        { id: 'START_DATE_TO_END_DATE' },
        {
          startDate: formattedStart,
          endDate: formattedEnd,
        },
      );
    } else if (formattedStart) {
      return intl.formatMessage(
        { id: 'START_DATE_TO_PRESENT' },
        {
          startDate: formattedStart,
        },
      );
    } else {
      return intl.formatMessage(
        { id: 'UNTIL_END_DATE' },
        {
          endDate: formattedEnd,
        },
      );
    }
  };

  function renderFilterLabel(typedFilter: FilterName): string {
    return FILTERS[nonGenericIdentity].length > 1
      ? intl.formatMessage({ id: TRANSLATIONS[typedFilter] }) + `: `
      : '';
  }

  const renderTags = () =>
    Object.entries(filters).map(([filter, value]) => {
      const typedFilter = filter as FilterName;
      if (TYPES.dateRange.includes(typedFilter)) {
        const dateRangeFilter = value as DateRange; //Since we checked it is indeed a dateRange, cast value as a DateRange
        if (!dateRangeFilter) {
          return null;
        }

        return (
          <Tag
            key={`activeFilter-${typedFilter}`}
            size="s"
            variant="primary"
            label={renderFilterLabel(typedFilter) + renderDateRangeLabel(dateRangeFilter)}
            onClick={() => handleClearFilter(typedFilter)}
            testId={`${identity}-activeFilter-${typedFilter}`}
          ></Tag>
        );
      } else {
        const typedValue = value as Exclude<typeof value, DateRange>;
        return Array.isArray(typedValue) ? (
          typedValue.map((mappedValue, index) => {
            return (
              <Tag
                key={`activeFilter-${typedFilter}-${index}`}
                size="s"
                variant="primary"
                label={renderFilterLabel(typedFilter) + mappedValue.label}
                onClick={() => handleClearFilter(typedFilter, mappedValue.value)}
                testId={`${identity}-activeFilter-${typedFilter}-${index}`}
              ></Tag>
            );
          })
        ) : (
          <Tag
            key={`activeFilter-${typedFilter}`}
            variant="primary"
            label={renderFilterLabel(typedFilter) + typedValue.label}
            size="s"
            onClick={() => handleClearFilter(typedFilter, typedValue.value)}
            testId={`${identity}-activeFilter-${typedFilter}`}
          ></Tag>
        );
      }
    });

  const renderClearButton = () => (
    <Button size="s" variant="link" onClick={handleClearIdentity} testId={`${identity}-clearAll`}>
      <FormattedMessage id="CLEAR_ALL" />
    </Button>
  );

  if (direction === 'row') {
    return (
      <div className={styles.root} style={{ margin }} data-testid={`${identity}-filterDisplay`}>
        <div className={styles.tagZone}>
          <div className={styles.label}>
            <FormattedMessage id="APPLIED_FILTERS" />:
          </div>
          {renderTags()}
        </div>
        {renderClearButton()}
      </div>
    );
  }

  return (
    <div
      className={`${styles.root} ${styles.column}`}
      style={{ margin }}
      data-testid={`${identity}-filterDisplay`}
    >
      <div className={styles.header}>
        <div className={styles.label}>
          <FormattedMessage id="APPLIED_FILTERS" />
        </div>
        {renderClearButton()}
      </div>
      <div className={styles.tagZone}>{renderTags()}</div>
    </div>
  );
};

export default FilterDisplay;
